<script>
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';

export default {
  props: {
    blockedPeriods: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      selectedStartHour: '12',
      selectedStartMinute: '00',
      selectedEndHour: '13',
      selectedEndMinute: '00',

      blockedPeriodsList: [...this.blockedPeriods],

      errorMessagesList: [
        'Horário inválido, por favor, selecione outro horário para esse intervalo',
        'O período que você tentou adicionar já existe',
        'O horário de início deve ser maior que o horário de término',
        'O intervalo mínimo é de 10 minutos',
      ],
      errorMessage: '',
    };
  },
  computed: {
    // List of minutes 0..55, step of 5 minutes
    selectableMinutes() {
      return Array.from({ length: 12 }, (_, i) =>
        (i * 5).toString().padStart(2, '0')
      );
    },
    // List of hours 0..23
    selectableHours() {
      return [...Array(24)].map((_, i) => i.toString().padStart(2, '0'));
    },
  },
  methods: {
    showAlert(message) {
      emitter.emit('newToastMessage', message);
    },
    addSelectedInterval() {
      const selectedStartTime = `${this.selectedStartHour}:${this.selectedStartMinute}`;
      const selectedEndTime = `${this.selectedEndHour}:${this.selectedEndMinute}`;
      if (!this.validSelectedInterval(selectedStartTime, selectedEndTime))
        useAlert('❌  - ' + this.errorMessage);
      else {
        this.blockedPeriodsList.push(`${selectedStartTime}-${selectedEndTime}`);
        this.emitUpdatedBlockedPeriods();
      }
    },
    removeSelectedInterval(selectedInterval) {
      this.blockedPeriodsList = this.blockedPeriodsList.filter(
        e => e !== selectedInterval
      );
      this.emitUpdatedBlockedPeriods();
    },
    validSelectedInterval(start, end) {
      const selectedStartHour = start.split(':')[0];
      const selectedStartMinute = start.split(':')[1];
      const selectedEndHour = end.split(':')[0];
      const selectedEndMinute = end.split(':')[1];

      // Function to compare the selected time with list of selectable times
      const validTime = (val, validList) => validList.some(e => e === val);
      if (
        !selectedStartHour ||
        !selectedStartMinute ||
        !selectedEndHour ||
        !selectedEndMinute ||
        !validTime(selectedEndHour, this.selectableHours) ||
        !validTime(selectedEndMinute, this.selectableMinutes) ||
        !validTime(selectedStartHour, this.selectableHours) ||
        !validTime(selectedStartMinute, this.selectableMinutes)
      ) {
        this.errorMessage = this.errorMessagesList[0];
        return false;
      }

      // Searching repeated periods in the list
      if (this.blockedPeriodsList.some(e => e === `${start}-${end}`)) {
        this.errorMessage = this.errorMessagesList[1];
        return false;
      }

      const greaterNumber = (startT, endT) => Number(startT) > Number(endT);
      if (
        start === end ||
        greaterNumber(selectedStartHour, selectedEndHour) ||
        (selectedStartHour === selectedEndHour &&
          greaterNumber(selectedStartMinute, selectedEndMinute))
      ) {
        this.errorMessage = this.errorMessagesList[2];
        return false;
      }

      // Period is equal or bigger than 10 minutes
      if (
        selectedStartHour === selectedEndHour &&
        Number(selectedEndMinute) - Number(selectedStartMinute) < 10
      ) {
        this.errorMessage = this.errorMessagesList[3];
        return false;
      }

      return true;
    },
    emitUpdatedBlockedPeriods() {
      this.$emit('updateBlockedPeriods', this.blockedPeriodsList);
    },
  },
};
</script>

<template>
  <div>
    <div class="column">
      <div class="column">
        <label class="blocked-periods-labels text-sm">COMEÇA ÀS:</label>
        <div class="row">
          <select
            v-model="selectedStartHour"
            class="select-time-blocked-periods text-sm"
          >
            <option disabled value="">Hora</option>
            <option v-for="hour in selectableHours" :key="hour">
              {{ hour }}
            </option>
          </select>
          <select
            v-model="selectedStartMinute"
            class="select-time-blocked-periods text-sm"
          >
            <option disabled value="">Minuto</option>
            <option v-for="minute in selectableMinutes" :key="minute">
              {{ minute }}
            </option>
          </select>
        </div>
        <label class="blocked-periods-labels text-sm">TERMINA ÀS:</label>
        <div class="row">
          <select
            v-model="selectedEndHour"
            class="select-time-blocked-periods text-sm"
          >
            <option disabled value="">Hora</option>
            <option v-for="hour in selectableHours" :key="hour">
              {{ hour }}
            </option>
          </select>
          <select
            v-model="selectedEndMinute"
            class="select-time-blocked-periods text-sm"
          >
            <option disabled value="">Minuto</option>
            <option v-for="minute in selectableMinutes" :key="minute">
              {{ minute }}
            </option>
          </select>
        </div>
      </div>

      <button
        class="button default-style w-145 h-30"
        :class="{ 'blocked-periods-button': !blockedPeriodsList.length }"
        @click.prevent="addSelectedInterval"
      >
        <fluent-icon icon="add" size="20" class="my-0 mx-auto" />
      </button>
    </div>
    <div
      v-for="(interval, index) in blockedPeriodsList"
      :key="interval"
      class="row"
    >
      <input
        class="default-style text-xs w-110"
        :class="{ 'grey-bg': index % 2 === 0 }"
        :style="{ color: index % 2 === 0 ? 'black' : '' }"
        type="text"
        readonly
        :value="interval"
      />
      <button
        class="button default-style text-sm w-35 custom-bg-color"
        @click.prevent="removeSelectedInterval(interval)"
      >
        x
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.w-35 {
  width: 110px;
}
.w-110 {
  width: 110px;
}
.w-145 {
  width: 145px;
}
.h-30 {
  height: 30px;
}
.custom-bg-color {
  background-color: var(--r-700);
}
.default-style {
  border: 0;
  height: 35px;
  border-radius: 0px;
  height: 35px;
  margin: 0;

  &.blocked-periods-button {
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
  }
}
input[readonly] {
  &:hover {
    cursor: pointer;
  }

  &.grey-bg {
    background-color: var(--b-100);
  }
}
button {
  box-shadow: 0px 1.5px 3px rgba(0, 0, 0, 0.486);
  transition: box-shadow 0.3s ease;
}

.blocked-periods-labels {
  color: black;
  border-left: 1px solid #ccd9e4;
  border-right: 1px solid #ccd9e4;
  padding: 5px 0;
  background-color: var(--b-100);
}
.select-time-blocked-periods {
  width: 72.5px;
  height: 35px;
  border-radius: 0px;
  margin: 0;
}
</style>
